import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ReportPackingListUniform.css';
import Swal from 'sweetalert2';
import { reportPackingListUniformAPI } from '../../services/api';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

// Register ag-grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * ReportPackingListUniform Component
 *
 * Displays packing list report for uniforms by class
 * Features:
 * - District and Activity filtering
 * - Grouped by School → Year → Class
 * - Shows uniform size distribution for all uniform types
 * - Excel/PDF export functionality
 */
const ReportPackingListUniform = () => {
  const { t } = useTranslation(['reports', 'common']);

  // ========== STATE MANAGEMENT ==========

  // Auth/User State
  const [user, setUser] = useState(null);
  const [resourcePermissions, setResourcePermissions] = useState(null);

  // Filter State
  const [districts, setDistricts] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');

  // Data State
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ag-grid State
  const [gridApi, setGridApi] = useState(null);

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // ========== AG-GRID CONFIGURATION ==========

  // Column definitions for ag-grid with grouping
  const columnDefs = useMemo(() => [
    {
      headerName: 'NO',
      valueGetter: 'node.rowIndex + 1',
      width: 70,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: 'Kod',
      field: 'kod',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: 'Sekolah',
      field: 'sekolah',
      width: 250,
      // rowGroup: true, // ❌ Removed - requires AG Grid Enterprise
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: 'TAHUN',
      field: 'tahun',
      width: 100,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: 'CLASS',
      field: 'class',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    // Baju Putih
    {
      headerName: 'Baju Putih S',
      field: 'bajuPutih_S',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Putih M',
      field: 'bajuPutih_M',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Putih L',
      field: 'bajuPutih_L',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Putih XL',
      field: 'bajuPutih_XL',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Putih XXL',
      field: 'bajuPutih_XXL',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Putih XXXL',
      field: 'bajuPutih_XXXL',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Putih Jumlah',
      field: 'bajuPutih_Jumlah',
      width: 130,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    },
    // Pinafore
    {
      headerName: 'Pinafore S',
      field: 'pinafore_S',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Pinafore M',
      field: 'pinafore_M',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Pinafore L',
      field: 'pinafore_L',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Pinafore XL',
      field: 'pinafore_XL',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Pinafore XXL',
      field: 'pinafore_XXL',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Pinafore XXXL',
      field: 'pinafore_XXXL',
      width: 110,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Pinafore Jumlah',
      field: 'pinafore_Jumlah',
      width: 130,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    },
    // Seluar Panjang
    {
      headerName: 'Seluar Panjang S',
      field: 'seluarPanjang_S',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Panjang M',
      field: 'seluarPanjang_M',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Panjang L',
      field: 'seluarPanjang_L',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Panjang XL',
      field: 'seluarPanjang_XL',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Panjang XXL',
      field: 'seluarPanjang_XXL',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Panjang XXXL',
      field: 'seluarPanjang_XXXL',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Panjang Jumlah',
      field: 'seluarPanjang_Jumlah',
      width: 150,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    },
    // Baju Kurung
    {
      headerName: 'Baju Kurung S',
      field: 'bajuKurung_S',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Kurung M',
      field: 'bajuKurung_M',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Kurung L',
      field: 'bajuKurung_L',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Kurung XL',
      field: 'bajuKurung_XL',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Kurung XXL',
      field: 'bajuKurung_XXL',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Kurung XXXL',
      field: 'bajuKurung_XXXL',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Baju Kurung Jumlah',
      field: 'bajuKurung_Jumlah',
      width: 140,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    },
    // Seluar Pendek
    {
      headerName: 'Seluar Pendek S',
      field: 'seluarPendek_S',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Pendek M',
      field: 'seluarPendek_M',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Pendek L',
      field: 'seluarPendek_L',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Pendek XL',
      field: 'seluarPendek_XL',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Pendek XXL',
      field: 'seluarPendek_XXL',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Pendek XXXL',
      field: 'seluarPendek_XXXL',
      width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Seluar Pendek Jumlah',
      field: 'seluarPendek_Jumlah',
      width: 150,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    },
    // Kain Kurung
    {
      headerName: 'Kain Kurung S',
      field: 'kainKurung_S',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kain Kurung M',
      field: 'kainKurung_M',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kain Kurung L',
      field: 'kainKurung_L',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kain Kurung XL',
      field: 'kainKurung_XL',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kain Kurung XXL',
      field: 'kainKurung_XXL',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kain Kurung XXXL',
      field: 'kainKurung_XXXL',
      width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kain Kurung Jumlah',
      field: 'kainKurung_Jumlah',
      width: 140,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    },
    // Gender totals
    {
      headerName: 'Lelaki',
      field: 'lelaki',
      width: 100,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Perempuan',
      field: 'perempuan',
      width: 100,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    }
  ], []);

  // Default column configuration
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
    wrapHeaderText: true,
    autoHeaderHeight: true
  }), []);

  // Grid options with grouping
  const gridOptions = useMemo(() => ({
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [30, 50, 100, 200],
    domLayout: 'autoHeight',
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    groupDisplayType: 'multipleColumns',
    groupDefaultExpanded: 0,
    autoGroupColumnDef: {
      headerName: 'Group',
      minWidth: 300,
      cellRendererParams: {
        suppressCount: false
      }
    }
  }), []);

  // Grid ready callback
  const onGridReady = (params) => {
    setGridApi(params.api);
    console.log('[ReportPackingListUniform] ag-grid ready with grouping');
  };

  // ========== EFFECTS ==========

  // Load user and permissions on mount
  useEffect(() => {
    loadUser();
    loadResourcePermissions();
  }, []);

  // Load districts when user is loaded
  useEffect(() => {
    if (user) {
      loadDistricts();
    }
  }, [user]);

  // Load activities when district changes
  useEffect(() => {
    if (selectedDistrict) {
      loadActivities(selectedDistrict);
      setSelectedActivity('');
      setReportData(null);
    }
  }, [selectedDistrict]);

  // Auto-load data when activity changes
  useEffect(() => {
    if (selectedActivity && selectedDistrict) {
      console.log('[ReportPackingListUniform] Activity selected, auto-loading data');
      loadReportData(selectedDistrict, selectedActivity);
    }
  }, [selectedActivity]);

  // ========== DATA LOADING FUNCTIONS ==========

  const loadUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        setUser(userData);
        console.log('[ReportPackingListUniform] User loaded:', userData.email);
      } else {
        console.error('[ReportPackingListUniform] No user found in localStorage');
        setError('User not found. Please login again.');
      }
    } catch (error) {
      console.error('[ReportPackingListUniform] Error loading user:', error);
      setError('Failed to load user information');
    }
  };

  const loadResourcePermissions = () => {
    try {
      const permsStr = localStorage.getItem('resourcePermissions');
      if (permsStr) {
        const perms = JSON.parse(permsStr);
        setResourcePermissions(perms);
        console.log('[ReportPackingListUniform] Resource permissions loaded');
      }
    } catch (error) {
      console.error('[ReportPackingListUniform] Error loading permissions:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      console.log('[ReportPackingListUniform] Loading districts...');
      const response = await reportPackingListUniformAPI.getDistricts();

      if (response.success && response.data) {
        setDistricts(response.data);
        console.log('[ReportPackingListUniform] Loaded', response.data.length, 'districts');

        // Auto-select if only one district
        if (response.data.length === 1) {
          setSelectedDistrict(response.data[0].ppd);
        }
      } else {
        console.error('[ReportPackingListUniform] Failed to load districts:', response);
        setError('Failed to load districts');
      }
    } catch (error) {
      console.error('[ReportPackingListUniform] Error loading districts:', error);
      setError('Error loading districts: ' + error.message);
    }
  };

  const loadActivities = async (districtCode) => {
    try {
      console.log('[ReportPackingListUniform] Loading activities for district:', districtCode);
      const response = await reportPackingListUniformAPI.getActivities(districtCode);

      if (response.success && response.data) {
        setActivities(response.data);
        console.log('[ReportPackingListUniform] Loaded', response.data.length, 'activities');

        // Auto-select first active activity
        const activeActivity = response.data.find(a => a.status === 'ACTIVE');
        if (activeActivity) {
          setSelectedActivity(activeActivity.activityId.toString());
        } else if (response.data.length > 0) {
          setSelectedActivity(response.data[0].activityId.toString());
        }
      } else {
        console.error('[ReportPackingListUniform] Failed to load activities:', response);
        setError('Failed to load activities');
      }
    } catch (error) {
      console.error('[ReportPackingListUniform] Error loading activities:', error);
      setError('Error loading activities: ' + error.message);
    }
  };

  const loadReportData = async (districtCode, activityId) => {
    try {
      setLoading(true);
      setError('');
      console.log('[ReportPackingListUniform] Loading report data for district:', districtCode, 'activity:', activityId);

      const response = await reportPackingListUniformAPI.getReportData(districtCode, parseInt(activityId));

      if (response.success && response.data) {
        setReportData(response.data);
        console.log('[ReportPackingListUniform] Report data loaded:', response.data.totalStudents, 'students from', response.data.totalSchools, 'schools');
      } else {
        console.error('[ReportPackingListUniform] Failed to load report data:', response);
        setError(response.message || 'Failed to load report data');
        setReportData(null);
      }
    } catch (error) {
      console.error('[ReportPackingListUniform] Error loading report data:', error);
      setError('Error loading report data: ' + error.message);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // ========== EXPORT FUNCTIONS ==========

  const handleExportExcel = async () => {
    // Validation
    if (!selectedDistrict || !selectedActivity) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.selectionRequired'),
        text: t('reports:packingListUniform.messages.selectDistrictActivity'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      Swal.fire({
        title: t('reports:common.messages.generatingExcel'),
        html: t('reports:common.messages.pleaseWait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await reportPackingListUniformAPI.exportToExcel({
        DistrictCode: selectedDistrict,
        ActivityId: parseInt(selectedActivity),
        SplitBy: 1,
        IncludeSubtotals: true,
        IncludeGrandTotal: true,
        ShowPageHeaders: true,
        ApplyFormatting: true
      });

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        text: t('reports:common.messages.excelDownloaded'),
        confirmButtonColor: '#28a745'
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: error.response?.data?.message || error.message || 'Unknown error',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleExportPDF = async () => {
    if (!reportData || !reportData.rows || reportData.rows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.noData'),
        text: t('reports:common.messages.noDataToExport'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      console.log('[ReportPackingListUniform] Exporting to PDF (Client-side jsPDF)...');

      Swal.fire({
        title: t('reports:common.messages.generatingPDF'),
        text: t('reports:common.messages.pleaseWaitPDF'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for wide table
      let yPosition = 15;

      // Add company name
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('KIM TECK CHEONG MULIA SDN BHD', pdf.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Add title
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'bold');
      pdf.text('Packing List - Uniform by Class', pdf.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Add report info
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');

      // Left column
      const leftX = 14;
      const rightX = pdf.internal.pageSize.getWidth() / 2 + 10;
      let leftY = yPosition;
      let rightY = yPosition;

      pdf.text(`DAERAH: ${reportData.districtCode}`, leftX, leftY);
      leftY += 5;
      pdf.text(`AKTIVITI: ${reportData.activityName} (${reportData.year})`, leftX, leftY);
      leftY += 5;
      pdf.text(`BIL. SEKOLAH: ${reportData.totalSchools}`, leftX, leftY);
      leftY += 5;
      pdf.text(`BIL. PELAJAR: ${reportData.totalStudents}`, leftX, leftY);
      leftY += 5;

      // Get user data for printed info
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : null;
      const userRole = userData?.role || 'Unknown';

      // Right column - Printed info
      const currentDate = new Date();
      const dateStr = currentDate.toLocaleDateString('en-MY', { year: 'numeric', month: '2-digit', day: '2-digit' });
      const timeStr = currentDate.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

      pdf.text(`TARIKH CETAK: ${dateStr}`, rightX, rightY);
      rightY += 5;
      pdf.text(`MASA CETAK: ${timeStr}`, rightX, rightY);
      rightY += 5;
      pdf.text(`DICETAK OLEH: ${userData?.username || 'Unknown'} (${userRole})`, rightX, rightY);
      rightY += 5;

      yPosition = Math.max(leftY, rightY) + 3;

      // Group data by school
      const schoolGroups = {};
      reportData.rows.forEach(row => {
        const schoolKey = `${row.kod}|${row.sekolah}`;
        if (!schoolGroups[schoolKey]) {
          schoolGroups[schoolKey] = {
            kod: row.kod,
            sekolah: row.sekolah,
            lelaki: 0,
            perempuan: 0,
            rows: []
          };
        }
        schoolGroups[schoolKey].rows.push(row);
        schoolGroups[schoolKey].lelaki += row.lelaki || 0;
        schoolGroups[schoolKey].perempuan += row.perempuan || 0;
      });

      const schools = Object.values(schoolGroups);
      let schoolIndex = 1;

      schools.forEach((school, idx) => {
        // School header box (3 rows - border only, no background)
        const headerHeight = 16;
        pdf.setDrawColor(0, 0, 0); // Black border
        pdf.setLineWidth(0.2); // Standard border weight
        pdf.rect(14, yPosition, pdf.internal.pageSize.getWidth() - 28, headerHeight, 'S');

        pdf.setFontSize(9);
        pdf.setFont(undefined, 'bold');

        // Row 1: School Code + Page Number
        pdf.text(`KOD SEKOLAH: ${school.kod}`, 16, yPosition + 4);
        pdf.text(`HALAMAN ${schoolIndex} DARIPADA ${schools.length}`, pdf.internal.pageSize.getWidth() - 16, yPosition + 4, { align: 'right' });

        // Row 2: School Name + Student Counts
        pdf.setFont(undefined, 'normal');
        pdf.text(`NAMA SEKOLAH: ${school.sekolah}`, 16, yPosition + 9);
        const jumlahPelajar = school.lelaki + school.perempuan;
        pdf.text(`LELAKI: ${school.lelaki}  PEREMPUAN: ${school.perempuan}  JUMLAH: ${jumlahPelajar}`, pdf.internal.pageSize.getWidth() - 16, yPosition + 9, { align: 'right' });

        // Row 3: District + KARUNG (right-aligned)
        pdf.text(`DAERAH: ${reportData.districtCode}`, 16, yPosition + 14);
        pdf.text(`KARUNG: __________`, pdf.internal.pageSize.getWidth() - 16, yPosition + 14, { align: 'right' });

        yPosition += headerHeight + 2;

        // SECTION 1: Baju Putih, Seluar Panjang, Seluar Pendek
        const table1Headers = [
          [
            { content: 'NO', rowSpan: 2 },
            { content: 'TAHUN', rowSpan: 2 },
            { content: 'KELAS', rowSpan: 2 },
            { content: 'BAJU PUTIH', colSpan: 7 },
            { content: 'SELUAR PANJANG', colSpan: 7 },
            { content: 'SELUAR PENDEK', colSpan: 7 }
          ],
          [
            'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'JML',  // Baju Putih
            'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'JML',  // Seluar Panjang
            'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'JML'   // Seluar Pendek
          ]
        ];

        const table1Data = school.rows.map((row, index) => [
          index + 1,
          row.tahun || '',
          row.class || '',
          // Baju Putih
          row.bajuPutih_S || 0, row.bajuPutih_M || 0, row.bajuPutih_L || 0,
          row.bajuPutih_XL || 0, row.bajuPutih_XXL || 0, row.bajuPutih_XXXL || 0, row.bajuPutih_Jumlah || 0,
          // Seluar Panjang
          row.seluarPanjang_S || 0, row.seluarPanjang_M || 0, row.seluarPanjang_L || 0,
          row.seluarPanjang_XL || 0, row.seluarPanjang_XXL || 0, row.seluarPanjang_XXXL || 0, row.seluarPanjang_Jumlah || 0,
          // Seluar Pendek
          row.seluarPendek_S || 0, row.seluarPendek_M || 0, row.seluarPendek_L || 0,
          row.seluarPendek_XL || 0, row.seluarPendek_XXL || 0, row.seluarPendek_XXXL || 0, row.seluarPendek_Jumlah || 0
        ]);

        // Calculate totals for Section 1
        const totals1 = {
          bp: [0, 0, 0, 0, 0, 0, 0],
          sp: [0, 0, 0, 0, 0, 0, 0],
          spendek: [0, 0, 0, 0, 0, 0, 0]
        };
        school.rows.forEach(row => {
          totals1.bp[0] += row.bajuPutih_S || 0;
          totals1.bp[1] += row.bajuPutih_M || 0;
          totals1.bp[2] += row.bajuPutih_L || 0;
          totals1.bp[3] += row.bajuPutih_XL || 0;
          totals1.bp[4] += row.bajuPutih_XXL || 0;
          totals1.bp[5] += row.bajuPutih_XXXL || 0;
          totals1.bp[6] += row.bajuPutih_Jumlah || 0;

          totals1.sp[0] += row.seluarPanjang_S || 0;
          totals1.sp[1] += row.seluarPanjang_M || 0;
          totals1.sp[2] += row.seluarPanjang_L || 0;
          totals1.sp[3] += row.seluarPanjang_XL || 0;
          totals1.sp[4] += row.seluarPanjang_XXL || 0;
          totals1.sp[5] += row.seluarPanjang_XXXL || 0;
          totals1.sp[6] += row.seluarPanjang_Jumlah || 0;

          totals1.spendek[0] += row.seluarPendek_S || 0;
          totals1.spendek[1] += row.seluarPendek_M || 0;
          totals1.spendek[2] += row.seluarPendek_L || 0;
          totals1.spendek[3] += row.seluarPendek_XL || 0;
          totals1.spendek[4] += row.seluarPendek_XXL || 0;
          totals1.spendek[5] += row.seluarPendek_XXXL || 0;
          totals1.spendek[6] += row.seluarPendek_Jumlah || 0;
        });

        // Add totals row
        table1Data.push([
          '', '', 'JUMLAH',
          ...totals1.bp, ...totals1.sp, ...totals1.spendek
        ]);

        // Table 1
        autoTable(pdf, {
          head: table1Headers,
          body: table1Data,
          startY: yPosition,
          theme: 'grid',
          styles: {
            fontSize: 9,
            cellPadding: 1,
            halign: 'center',
            valign: 'middle',
            textColor: 0, // Black text (not gray)
            lineColor: [0, 0, 0],
            lineWidth: 0.2 // Same as header box border
          },
          headStyles: {
            fillColor: [255, 255, 255], // White background
            textColor: 0, // Black text
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 9,
            lineColor: [0, 0, 0], // Black borders
            lineWidth: 0.2 // Same as header box border
          },
          columnStyles: {
            0: { cellWidth: 8, halign: 'center' },    // NO
            1: { halign: 'center' },                   // TAHUN - auto-fit
            2: { halign: 'left' },                     // KELAS - auto-fit
            // Size columns auto-fit equally (no explicit width)
            9: { fontStyle: 'bold' },   // BP Jumlah
            16: { fontStyle: 'bold' },  // SP Jumlah
            23: { fontStyle: 'bold' }   // SPendek Jumlah
          },
          margin: { left: 3, right: 3 },
          didParseCell: function(data) {
            // Bold the JUMLAH row
            if (data.section === 'body' && data.row.index === table1Data.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              // No background color for B&W printing
            }
          },
          didDrawPage: function (data) {
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'normal');
            const pageStr = `Halaman ${pdf.internal.getCurrentPageInfo().pageNumber}`;
            pdf.text(pageStr, pdf.internal.pageSize.getWidth() - 30, pdf.internal.pageSize.getHeight() - 10);
          }
        });

        yPosition = pdf.lastAutoTable.finalY + 5;

        // SECTION 2: Pinafore, Baju Kurung, Kain Kurung
        const table2Headers = [
          [
            { content: 'NO', rowSpan: 2 },
            { content: 'TAHUN', rowSpan: 2 },
            { content: 'KELAS', rowSpan: 2 },
            { content: 'PINAFORE', colSpan: 7 },
            { content: 'BAJU KURUNG', colSpan: 7 },
            { content: 'KAIN KURUNG', colSpan: 7 }
          ],
          [
            'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'JML',  // Pinafore
            'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'JML',  // Baju Kurung
            'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'JML'   // Kain Kurung
          ]
        ];

        const table2Data = school.rows.map((row, index) => [
          index + 1,
          row.tahun || '',
          row.class || '',
          // Pinafore
          row.pinafore_S || 0, row.pinafore_M || 0, row.pinafore_L || 0,
          row.pinafore_XL || 0, row.pinafore_XXL || 0, row.pinafore_XXXL || 0, row.pinafore_Jumlah || 0,
          // Baju Kurung
          row.bajuKurung_S || 0, row.bajuKurung_M || 0, row.bajuKurung_L || 0,
          row.bajuKurung_XL || 0, row.bajuKurung_XXL || 0, row.bajuKurung_XXXL || 0, row.bajuKurung_Jumlah || 0,
          // Kain Kurung
          row.kainKurung_S || 0, row.kainKurung_M || 0, row.kainKurung_L || 0,
          row.kainKurung_XL || 0, row.kainKurung_XXL || 0, row.kainKurung_XXXL || 0, row.kainKurung_Jumlah || 0
        ]);

        // Calculate totals for Section 2
        const totals2 = {
          pf: [0, 0, 0, 0, 0, 0, 0],
          bk: [0, 0, 0, 0, 0, 0, 0],
          kk: [0, 0, 0, 0, 0, 0, 0]
        };
        school.rows.forEach(row => {
          totals2.pf[0] += row.pinafore_S || 0;
          totals2.pf[1] += row.pinafore_M || 0;
          totals2.pf[2] += row.pinafore_L || 0;
          totals2.pf[3] += row.pinafore_XL || 0;
          totals2.pf[4] += row.pinafore_XXL || 0;
          totals2.pf[5] += row.pinafore_XXXL || 0;
          totals2.pf[6] += row.pinafore_Jumlah || 0;

          totals2.bk[0] += row.bajuKurung_S || 0;
          totals2.bk[1] += row.bajuKurung_M || 0;
          totals2.bk[2] += row.bajuKurung_L || 0;
          totals2.bk[3] += row.bajuKurung_XL || 0;
          totals2.bk[4] += row.bajuKurung_XXL || 0;
          totals2.bk[5] += row.bajuKurung_XXXL || 0;
          totals2.bk[6] += row.bajuKurung_Jumlah || 0;

          totals2.kk[0] += row.kainKurung_S || 0;
          totals2.kk[1] += row.kainKurung_M || 0;
          totals2.kk[2] += row.kainKurung_L || 0;
          totals2.kk[3] += row.kainKurung_XL || 0;
          totals2.kk[4] += row.kainKurung_XXL || 0;
          totals2.kk[5] += row.kainKurung_XXXL || 0;
          totals2.kk[6] += row.kainKurung_Jumlah || 0;
        });

        // Add totals row
        table2Data.push([
          '', '', 'JUMLAH',
          ...totals2.pf, ...totals2.bk, ...totals2.kk
        ]);

        // Table 2
        autoTable(pdf, {
          head: table2Headers,
          body: table2Data,
          startY: yPosition,
          theme: 'grid',
          styles: {
            fontSize: 9,
            cellPadding: 1,
            halign: 'center',
            valign: 'middle',
            textColor: 0, // Black text (not gray)
            lineColor: [0, 0, 0],
            lineWidth: 0.2 // Same as header box border
          },
          headStyles: {
            fillColor: [255, 255, 255], // White background
            textColor: 0, // Black text
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 9,
            lineColor: [0, 0, 0], // Black borders
            lineWidth: 0.2 // Same as header box border
          },
          columnStyles: {
            0: { cellWidth: 8, halign: 'center' },    // NO
            1: { halign: 'center' },                   // TAHUN - auto-fit
            2: { halign: 'left' },                     // KELAS - auto-fit
            // Size columns auto-fit equally (no explicit width)
            9: { fontStyle: 'bold' },   // PF Jumlah
            16: { fontStyle: 'bold' },  // BK Jumlah
            23: { fontStyle: 'bold' }   // KK Jumlah
          },
          margin: { left: 3, right: 3 },
          didParseCell: function(data) {
            // Bold the JUMLAH row - NO background color
            if (data.section === 'body' && data.row.index === table2Data.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              // No background color for B&W printing
            }
          },
          didDrawPage: function (data) {
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'normal');
            const pageStr = `Halaman ${pdf.internal.getCurrentPageInfo().pageNumber}`;
            pdf.text(pageStr, pdf.internal.pageSize.getWidth() - 30, pdf.internal.pageSize.getHeight() - 10);
          }
        });

        yPosition = pdf.lastAutoTable.finalY + 5;

        // Page break after each school except last
        if (idx < schools.length - 1) {
          pdf.addPage();
          yPosition = 15;
        }

        schoolIndex++;
      });

      // Save file
      const filename = `Packing_List_Uniform_${reportData.districtCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);

      Swal.close();
      console.log('[ReportPackingListUniform] PDF export completed (client-side jsPDF)');
    } catch (error) {
      console.error('[ReportPackingListUniform] PDF export failed:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: t('reports:common.messages.exportFailedMessage'),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('reports:common.messages.capturingScreenshot'),
        html: t('reports:common.messages.pleaseWaitScreenshot'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.captureFailed'),
        text: t('reports:common.messages.captureFailedMessage')
      });
    }
  };

  // ========== RENDER ==========

  return (
    <div className="report-packing-list-uniform-container">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2>{t('reports:packingListUniform.title')} ({t('reports:packingListUniform.titleEn')})</h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('common:reportIssue', { lng: 'en' })}>
          <i className="fas fa-bug"></i>
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="filters-container">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="ddlDistrict">{t('reports:packingListUniform.filters.districtPPD')}</label>
            <select
              id="ddlDistrict"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              disabled={loading}
              className="form-control"
            >
              <option value="">{t('reports:packingListUniform.filters.selectDistrict')}</option>
              {districts.map(district => (
                <option key={district.ppd} value={district.ppd}>
                  {district.ppd}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="ddlActivity">{t('reports:packingListUniform.filters.activityYear')}</label>
            <select
              id="ddlActivity"
              value={selectedActivity}
              onChange={(e) => setSelectedActivity(e.target.value)}
              disabled={loading || !selectedDistrict}
              className="form-control"
            >
              <option value="">{t('reports:packingListUniform.filters.selectActivity')}</option>
              {activities.map(activity => (
                <option key={activity.activityId} value={activity.activityId}>
                  {activity.activityDisplay}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <button
              onClick={() => loadReportData(selectedDistrict, selectedActivity)}
              disabled={loading || !selectedDistrict || !selectedActivity}
              className="btn btn-primary"
            >
              {loading ? t('reports:packingListUniform.buttons.loading') : t('reports:packingListUniform.buttons.generateReport')}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <>
          <div className="report-info">
            <div className="info-card">
              <span className="info-label">{t('reports:packingListUniform.stats.district')}</span>
              <span className="info-value">{reportData.districtCode}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListUniform.stats.activity')}</span>
              <span className="info-value">{reportData.activityName}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListUniform.stats.year')}</span>
              <span className="info-value">{reportData.year}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListUniform.stats.totalSchools')}</span>
              <span className="info-value">{reportData.totalSchools}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListUniform.stats.totalStudents')}</span>
              <span className="info-value">{reportData.totalStudents}</span>
            </div>
          </div>

          <div className="export-buttons">
            <button onClick={handleExportExcel} className="btn btn-success">
              {t('reports:packingListUniform.buttons.exportExcel')}
            </button>
            <button onClick={handleExportPDF} className="btn btn-danger">
              {t('reports:packingListUniform.buttons.exportPDF')}
            </button>
          </div>

          <div className="ag-grid-container ag-theme-alpine">
            <AgGridReact
              columnDefs={columnDefs}
              rowData={reportData.rows}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onGridReady={onGridReady}
            />
          </div>
        </>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: 'Report Packing List Uniform',
            selectedDistrict,
            selectedActivity,
            reportData
          }}
        />
      )}
    </div>
  );
};

export default ReportPackingListUniform;
